import React from "react";
import { useUserContext } from "../../../auth/userContextProvider/UserContextProvider";
import {
    DotLegalInformationTooltip,
    DotLegalOverflowMenu,
    DotLegalPageHeader,
    DotLegalSwitch,
    DotLegalTableWithControls,
    ITableHeader,
    DotLegalButton,
} from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "../../../localization/useTranslation";
import { mapSecurityMeasureToSaveModel, useSecurityMeasureMapping } from "./SecurityMeasures.hooks";
import { useSecurityMeasuresStyles } from "./SecurityMeasures.styles";
import { Box, TableCell } from "@mui/material";
import TableRow from "@common/components/tableRow/TableRow";
import { SecurityMeasureViewModel, SecurityMeasureType } from "./SecurityMeasures.types";
import CheckIcon from "@mui/icons-material/Check";
import AddEditSecurityMeasureDialog from "../addEditSecurityMeasureDialog/AddEditSecurityMeasureDialog";
import { ChangeSecurityMeasureStatusDialog } from "../changeSecurityMeasureStatusDialog/ChangeSecurityMeasureStatusDialog";
import { DeleteSecurityMeasureDialog } from "../deleteSecurityMeasureDialog/DeleteSecurityMeasureDialog";

import { useMasterDataBreadCrumbProvider } from "../../useMasterDataBreadCrumbProvider";
import TablePagePaper from "@common/components/tablePagePaper/TablePagePaper";

function SecurityMeasures() {
    const styles = useSecurityMeasuresStyles();
    const { permissions } = useUserContext();
    const { translateString } = useTranslation();
    const { getBreadCrumbs } = useMasterDataBreadCrumbProvider();

    const {
        isLoading,
        securityMeasures,
        showAllSecurityMeasures,
        setShowAllSecurityMeasures,
        showAddEditDialog,
        setShowAddEditDialog,
        selectedSecurityMeasure,
        setSelectedSecurityMeasure,
        refetchSecurityMeasures,
        securityMeasureForDeletion,
        setSecurityMeasureForDeletion,
        securityMeasureForStatusChange,
        setSecurityMeasureForStatusChange,
    } = useSecurityMeasureMapping();

    const getMenuItems = (sm: SecurityMeasureViewModel) => {
        let menuItems = [];

        if (permissions.canManageMasterData) {
            menuItems.push({
                menuItem: translateString("edit"),
                onClick: () => {
                    setSelectedSecurityMeasure(sm);
                    setShowAddEditDialog(true);
                },
            });

            menuItems.push({
                menuItem: sm.isActive ? translateString("deactivate") : translateString("activate"),
                onClick: () => {
                    setSecurityMeasureForStatusChange(sm);
                },
            });
        }

        if (permissions.canManageMasterData && sm.isCustom) {
            menuItems.push({
                menuItem: translateString("removeSecurityMeasure"),
                onClick: () => {
                    setSecurityMeasureForDeletion(sm);
                },
            });
        }
        return menuItems;
    };

    const getGlobalTableCell = (row: SecurityMeasureViewModel) => {
        if (row.isGlobal)
            return (
                <React.Fragment>
                    <CheckIcon sx={styles.checkIcon} />
                    {(!row.hasAllBusinessAreas || !row.hasAllGroupEntities) && (
                        <DotLegalInformationTooltip
                            text={`${!row.hasAllBusinessAreas ? translateString("securityMeasureMissingBusinessAreas") : ""}
                                ${!row.hasAllGroupEntities ? translateString("securityMeasureMissingGroupEntites") : ""}`}
                        />
                    )}
                </React.Fragment>
            );

        return "";
    };

    return (
        <Box sx={styles.container}>
            <DotLegalPageHeader
                breadCrumbs={getBreadCrumbs(translateString("securityMeasures"))}
                leftComponent={
                    <DotLegalSwitch
                        onChange={setShowAllSecurityMeasures}
                        checked={showAllSecurityMeasures}
                        leftLabel={translateString("active")}
                        rightLabel={translateString("all")}
                    />
                }
                rightComponent={
                    permissions.canManageMasterData ? (
                        <DotLegalButton
                            buttonType="primary"
                            size="medium"
                            onClick={() => {
                                setShowAddEditDialog(true);
                                setSelectedSecurityMeasure(undefined);
                            }}
                        >
                            {translateString("createSecurityMeasure")}
                        </DotLegalButton>
                    ) : undefined
                }
            />

            <TablePagePaper>
                <DotLegalTableWithControls
                    internalScroll
                    headers={getHeaders(translateString)}
                    isLoading={isLoading}
                    defaultOrderBy={"name"}
                    defaultOrder={"asc"}
                    getUserSpecificPageLength={() => 30}
                    hideRowsPerPage
                    clickableRows={permissions.canManageMasterData}
                    hasOverflowMenu
                    labelRowsPerPage={translateString("showEntites")}
                    labelSearch={translateString("search")}
                    noOptionsLabel={translateString("noOptions")}
                    paginationLabelOf={translateString("labelOf")}
                    emptyText={translateString("noData")}
                    data={securityMeasures}
                    renderRow={(row, i) => (
                        <TableRow
                            key={i}
                            inactive={!row.isActive}
                            inactiveTooltip={translateString("deactivated")}
                            onClick={
                                permissions.canManageMasterData
                                    ? () => {
                                          setSelectedSecurityMeasure(row);
                                          setShowAddEditDialog(true);
                                      }
                                    : undefined
                            }
                        >
                            <TableCell sx={styles.bold}>{row.name}</TableCell>
                            <TableCell>{row.description}</TableCell>
                            <TableCell width={130}>{translateString(SecurityMeasureType[row.securityMeasureType as number].toLowerCase())}</TableCell>
                            <TableCell>
                                <Box sx={styles.iconContainer}>{getGlobalTableCell(row)}</Box>
                            </TableCell>
                            <TableCell>
                                <Box sx={styles.iconContainer}>{row.isActive ? <CheckIcon sx={styles.checkIcon} /> : ""}</Box>
                            </TableCell>
                            <TableCell>
                                <Box sx={styles.iconContainer}>{!row.isCustom ? <CheckIcon sx={styles.checkIcon} /> : ""}</Box>
                            </TableCell>
                            <TableCell align={"right"} onClick={(e) => e.stopPropagation()}>
                                <DotLegalOverflowMenu items={getMenuItems(row)} />
                            </TableCell>
                        </TableRow>
                    )}
                />
            </TablePagePaper>

            {securityMeasureForDeletion && (
                <DeleteSecurityMeasureDialog
                    selectedSecurityMeasure={securityMeasureForDeletion!}
                    onSaveCorrect={refetchSecurityMeasures}
                    onCloseDialog={() => setSecurityMeasureForDeletion(undefined)}
                />
            )}

            {securityMeasureForStatusChange && (
                <ChangeSecurityMeasureStatusDialog
                    selectedSecurityMeasure={securityMeasureForStatusChange!}
                    refetch={refetchSecurityMeasures}
                    onCloseDialog={() => setSecurityMeasureForStatusChange(undefined)}
                />
            )}
            {showAddEditDialog && (
                <AddEditSecurityMeasureDialog
                    selectSecurityMeasure={mapSecurityMeasureToSaveModel(selectedSecurityMeasure)}
                    selectedSecurityMeasureId={selectedSecurityMeasure?.id}
                    isAdd={selectedSecurityMeasure === undefined}
                    showGlobal
                    open={showAddEditDialog}
                    onDialogClose={() => setShowAddEditDialog(false)}
                    onOkClick={() => setShowAddEditDialog(false)}
                    refetch={refetchSecurityMeasures}
                />
            )}
        </Box>
    );
}

export default SecurityMeasures;

function getHeaders(translateString: any) {
    let headers = Array<ITableHeader<SecurityMeasureViewModel>>();
    headers.push({ property: "name", text: translateString("securityMeasure"), align: "left", showOnMobile: true });
    headers.push({ property: "description", text: translateString("description"), align: "left", showOnMobile: true });
    headers.push({ property: "securityMeasureType", text: translateString("type"), align: "left", showOnMobile: true });
    headers.push({ property: "isGlobal", text: translateString("global"), align: "left", showOnMobile: true });
    headers.push({ property: "isActive", text: translateString("active"), align: "left", showOnMobile: true });
    headers.push({ property: "isCustom", text: translateString("standardMasterData"), align: "left", showOnMobile: true });

    return headers;
}
